import { useState, useEffect, useRef } from "react";
import { X, Send, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHIPS = ["Old Delhi / Shahjahanabad", "Qutub Complex", "Nizamuddin", "Planning for a group"];
const GREETING =
  "Namaste. I am the Unzip Delhi concierge, an AI planning assistant. What would you like to explore about Delhi: its neighbourhoods, history, food or living traditions?";

export const Concierge = () => {
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const sessionRef = useRef(null);

  if (!sessionRef.current && typeof window !== "undefined") {
    let id = sessionStorage.getItem("ud_chat_session");
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem("ud_chat_session", id);
    }
    sessionRef.current = id;
  }

  useEffect(() => {
    if (sessionStorage.getItem("ud_concierge_seen")) return;
    const show = () => {
      setInvite(true);
      sessionStorage.setItem("ud_concierge_seen", "1");
    };
    const timer = setTimeout(show, 15000);
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && window.scrollY / h > 0.5) {
        show();
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("Chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let leftover = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        leftover += decoder.decode(value, { stream: true });
        const lines = leftover.split("\n");
        leftover = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          const payload = ln.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            acc += JSON.parse(payload).t;
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: acc };
              return c;
            });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          content: "I am having trouble connecting right now. Please email unzip.delhi@gmail.com and the team will help you.",
        };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {invite && !open && (
        <div
          data-testid="concierge-invite"
          className="fixed bottom-24 right-6 z-[70] w-72 bg-ivory border border-gold/40 shadow-xl p-5"
        >
          <button
            data-testid="concierge-dismiss-button"
            onClick={() => setInvite(false)}
            className="absolute top-3 right-3 text-maroon/50 hover:text-maroon"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
          <p className="font-accent text-[10px] tracking-[0.3em] text-gold mb-2">UNZIP CONCIERGE</p>
          <p className="text-sm text-[#5C4F52] leading-relaxed mb-4">
            Curious about Delhi? I can help you explore the experiences.
          </p>
          <button
            data-testid="concierge-open-button"
            onClick={() => { setInvite(false); setOpen(true); }}
            className="w-full py-2.5 bg-maroon text-ivory text-xs tracking-[0.2em] uppercase hover:bg-maroon-light transition-colors"
          >
            Ask the Concierge
          </button>
        </div>
      )}

      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-[80] w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[70vh] bg-ivory border border-gold/40 shadow-2xl flex flex-col"
        >
          <div className="bg-maroon text-ivory px-5 py-4 flex items-start justify-between shrink-0">
            <div>
              <p className="font-accent text-xs tracking-[0.25em]">UNZIP CONCIERGE</p>
              <p className="text-[11px] text-ivory/80 mt-1" data-testid="concierge-disclosure">AI planning assistant. Our team confirms all arrangements.</p>
            </div>
            <button data-testid="concierge-close-button" onClick={() => setOpen(false)} aria-label="Close chat"
              className="text-ivory/70 hover:text-ivory mt-0.5">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="concierge-messages" role="log" aria-label="Concierge conversation">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`concierge-message-${i}`} className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-maroon text-ivory"
                    : "bg-[#F6F0E6] text-[#1A0F12] border border-gold/25"
                }`}>
                  {m.content || (busy && i === messages.length - 1 ? "..." : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {messages.length === 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2 shrink-0" data-testid="concierge-chips">
              {CHIPS.map((c) => (
                <button key={c} data-testid={`concierge-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => send(`We are interested in: ${c}`)}
                  className="px-3 py-1.5 text-xs border border-maroon/30 text-maroon hover:bg-maroon hover:text-ivory transition-colors">
                  {c}
                </button>
              ))}
            </div>
          )}

          <div className="px-4 pb-2 shrink-0">
            <button data-testid="concierge-handoff-button" onClick={() => { setOpen(false); setInvite(false); navigate("/contact?interest=Cultural%20enquiry"); }}
              className="text-xs text-gold hover:text-maroon tracking-[0.15em] uppercase transition-colors">
              Prefer a human? Talk to the team
            </button>
          </div>

          <form
            className="p-3 border-t border-gold/25 flex gap-2 shrink-0"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <input
              data-testid="concierge-input"
              aria-label="Your question for the concierge"
              maxLength={1000}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about journeys, dates, groups"
              className="flex-1 bg-transparent px-3 py-2.5 text-sm text-[#1A0F12] placeholder:text-[#5C4F52]/50"
            />
            <button data-testid="concierge-send-button" type="submit" disabled={busy}
              className="w-10 h-10 bg-maroon text-ivory flex items-center justify-center hover:bg-maroon-light transition-colors disabled:opacity-50"
              aria-label="Send">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}

      {!open && (
        <button
          data-testid="concierge-button"
          onClick={() => { setInvite(false); setOpen(true); }}
          aria-label="Open concierge chat"
          className="fixed bottom-5 right-5 z-[70] w-12 h-12 bg-obsidian text-gold flex items-center justify-center border border-gold/40 hover:bg-carddark transition-colors duration-200"
        >
          <MessageSquare size={22} />
        </button>
      )}
    </>
  );
};
